import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import {
  CreateAreaRequest,
  ListAreasQuery,
  UpdateAreaRequest,
} from './Areas.types';

import {
  listAreas,
  deleteArea,
  createArea,
  updateArea,
  getArea,
  activateOrInactivateArea,
} from './Areas.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { useIntl } from 'react-intl';
import { message, notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';

export function useAreas(params?: ListAreasQuery) {
  const query = useInfiniteQuery(
    ['areas', { ...params }],
    ({ pageParam }) => {
      const req: ListAreasQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listAreas(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useArea(id?: number) {
  const query = useQuery(['area', id], () => (id ? getArea(id) : undefined), {
    enabled: !!id,
  });

  useServiceError(query.error);

  return query;
}

export function useAreaMutation() {
  const query = useMutation((id?: number) => getArea(id));

  useServiceError(query.error);

  return query;
}

export function useCreateArea() {
  const intl = useIntl();

  const mutation = useMutation((obj: CreateAreaRequest) => createArea(obj), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'areas.create.notification.success',
        }),
        description: intl.formatMessage({
          id: 'areas.create.notification.successDescription',
        }),
      });
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Already a Sub Group with the name:')
              ? 'Já existe essa área cadastrada no sistema!'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useUpdateArea() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation((obj: UpdateAreaRequest) => updateArea(obj), {
    onSuccess(_, data) {
      notification.success({
        message: intl.formatMessage({
          id: 'areas.update.notification.success',
        }),
        description: intl.formatMessage({
          id: 'areas.update.notification.successDescription',
        }),
      });

      queryClient.invalidateQueries(['area', data.id]);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Already a Sub Group with the name:')
              ? 'Já existe essa área cadastrada no sistema!'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useDeleteArea() {
  const intl = useIntl();

  const mutation = useMutation((id: number) => deleteArea(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'areas.update.notification.success',
        }),
        description: intl.formatMessage({
          id: 'areas.update.notification.successDescription',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useActivateOrInactivateArea() {
  const intl = useIntl();

  const mutation = useMutation(
    (areaId: number) => activateOrInactivateArea(areaId),

    {
      onSuccess() {
        message.success(
          intl.formatMessage({
            id: 'areas.status.message.success',
          }),
        );
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
